import React from "react";
import { uniqueId } from "lodash";
import { View, KeyboardAvoidingView, FlatList, Text } from "react-native";

import Utils, {
  escapeRegexp,
  hasItems,
  hasOneItem,
  isAndroid,
  navigate,
} from "../../../helpers/utils";
import appStyles from "../../appStyles";

import Search from "../common/search-bar/Search";
import IntegrationWrapper from "./details/IntegrationWrapper";
import Header from "../common/header/index";
import IntegrationsMappingFabButton from "./mapping/IntegrationsMappingFabButton";
import DeleteRuleConfirmDialog from "./delete/DeleteRuleConfirmDialog";
import IntegrationsHelper from "./IntegrationsHelper";
import RoundCheckbox from "../common/RoundCheckbox";
import appPages from "../../../appPages";
import styles from "./details/integrationStyles";

export default class IntegrationsDeletePage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    const params = this.props.route.params || {};
    const integrations = params.integrations || [];
    const preSelect = params.preSelect || false;
    const isDeletePage = props.templateName == "Delete";
    const isSingleIntegration = hasOneItem(integrations);

    // TODO: remove after integrations get id from API
    integrations.forEach((int) => {
      if (!int.id) int.id = uniqueId();
    });

    let selectedIntegrationsHash = {};
    if (isSingleIntegration || preSelect) {
      integrations.forEach((int) => {
        const rulesModel = IntegrationsHelper.getIntegrationRules(int);

        selectedIntegrationsHash[int.id] = {
          ...int,
          rules: rulesModel?.rules,
          rule: rulesModel?.defaultRule,
        };
      });
    }

    this.state = {
      isDeletePage,
      isSingleIntegration,
      showConfirmDialog: isSingleIntegration && isDeletePage,
      integrations,
      filteredIntegrations: integrations,
      selectedIntegrationsHash,
      selectedIds: Object.keys(selectedIntegrationsHash),
    };
  }

  goBack = () => {
    this.controller.goBack();
  };

  filterByKeyword = ({ term = "" }) => {
    const { integrations } = this.state;

    const pattern = Utils.getSearchPattern(term);
    const filteredIntegrations = term
      ? integrations.filter((a) => {
          return a.name && a.name.match(pattern);
        })
      : integrations;

    this.setState((state) => ({
      ...state,
      isLoading: false,
      filteredIntegrations,
      selectedIds: [],
    }));
  };

  toggleIntegrationSelection = (integration, is_selected) => {
    const { selectedIntegrationsHash, filteredIntegrations } = this.state;

    if (!integration.id) return;

    if (!is_selected) {
      selectedIntegrationsHash[integration.id] = integration;
    } else {
      delete selectedIntegrationsHash[integration.id];
    }

    this.setState((state) => ({
      ...state,
      filteredIntegrations: [...filteredIntegrations],
      selectedIntegrationsHash,
      selectedIds: Object.keys(selectedIntegrationsHash),
    }));
  };

  isValidModel = () => {
    const { selectedIds } = this.state;
    return hasItems(selectedIds);
  };

  toggleAll = (isSelected) => {
    const { integrations } = this.state;

    let selectedIntegrationsHash = {};
    if (!isSelected) {
      integrations.forEach((int) => {
        selectedIntegrationsHash[int.id] = int;
      });
    }

    this.setState((state) => ({
      ...state,
      selectedIntegrationsHash,
      selectedIds: Object.keys(selectedIntegrationsHash),
    }));
  };

  onButtonPress = () => {
    const { isDeletePage } = this.state;

    if (isDeletePage) {
      this.toggleConfirmDialog(true);
    } else {
      this.ignore();
    }
  };

  deleteIntegrations = () => {
    const { selectedIds } = this.state;

    this.toggleConfirmDialog(null);
    if (!hasItems(selectedIds)) return;

    IntegrationsHelper.deleteEntries(this.controller, selectedIds).then(() => {
      // Refresh
      //this.controller.getItems();

      this.goBack();
    });
  };

  ignore = () => {
    const { selectedIntegrationsHash } = this.state;

    this.toggleConfirmDialog(null);

    const rulesToIgnore = Object.values(selectedIntegrationsHash).map(
      (int) => ({ ...int.rule, id: int.matching_rule }),
    );

    if (!hasItems(rulesToIgnore)) return;

    IntegrationsHelper.stopGathering(this.controller, rulesToIgnore).then(
      () => {
        // Go to rules page
        navigate(this.controller, appPages.integrationRulesPage, "changePage", {
          selectedIds: rulesToIgnore.map((r) => r.id),
          tab: "ignore",
        });
      },
    );
  };

  goToIgnorePage = () => {
    const { selectedIntegrationsHash } = this.state;

    this.toggleConfirmDialog(null);

    navigate(this.controller, appPages.integrationsIgnorePage, "changePage", {
      integrations: Object.values(selectedIntegrationsHash),
      preSelect: true,
    });
  };

  toggleConfirmDialog = (showConfirmDialog) => {
    this.setState((state) => ({ ...state, showConfirmDialog }));
  };

  cancel = () => {
    this.toggleConfirmDialog(null);
    this.goBack();
  };

  // ------------------------------------- RENDER -------------------------------------
  render() {
    const {
      isLoading,
      integrations,
      filteredIntegrations,
      showConfirmDialog,
      selectedIds,
      isDeletePage,
      selectedIntegrationsHash,
      isSingleIntegration,
    } = this.state;

    const allSelected =
      hasItems(integrations) && integrations.length == selectedIds.length;
    const hasChildrenChecked = !allSelected && selectedIds.lenght > 0;
    const selectedToDelete = Object.values(selectedIntegrationsHash);

    const title = isDeletePage ? "Delete Information" : "Ignore this Data";
    const subTitle = isDeletePage
      ? "Select the integration data you want to delete"
      : "Select the integration data you want to ignore";

    const buttonText = isDeletePage
      ? "Delete Integration Data"
      : "Ignore this Data";

    const ListHeader = isSingleIntegration ? null : (
      <View style={styles.listHeader}>
        <Text style={styles.listHeaderText}>Integration Data</Text>
        <RoundCheckbox
          size={16}
          checked={allSelected}
          hasChildrenChecked={hasChildrenChecked}
          onValueChange={() => this.toggleAll(allSelected)}
        />
      </View>
    );

    return (
      <View style={[appStyles.container, { backgroundColor: "#FFF" }]}>
        <KeyboardAvoidingView
          style={appStyles.kbViewNoFooter}
          behavior="height"
          keyboardVerticalOffset={isAndroid ? 30 : 0}
        >
          <Header
            title={title}
            subTitle={subTitle}
            isLoading={isLoading}
            goBack={this.goBack}
          />

          {/* Integrations */}
          {hasItems(filteredIntegrations) ? (
            <FlatList
              ListHeaderComponent={ListHeader}
              style={[styles.flatList]}
              data={filteredIntegrations}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <IntegrationWrapper
                  controller={this.controller}
                  data={item}
                  showDate
                  showSelector
                  hasRules={!isDeletePage}
                  isSelected={!!selectedIntegrationsHash[item.id]}
                  onToggle={this.toggleIntegrationSelection}
                  style={{
                    marginBottom: 0,
                    paddingVertical: 4,
                    paddingLeft: 14,
                    paddingRight: 0,
                  }}
                />
              )}
            />
          ) : null}

          <View style={[appStyles.footerOptions]}>
            <Search
              showIcon={false}
              controller={this.controller}
              placeholder="Search Data"
              filterResults={this.filterByKeyword}
              buttonOptions={{
                isMainButton: true,
                disabled: !this.isValidModel(),
                style: { height: 40, width: 360, marginTop: 11 },
                text: buttonText,
                onPress: this.onButtonPress,
              }}
            />
          </View>
        </KeyboardAvoidingView>

        <View style={[appStyles.footerOptions]}>
          <IntegrationsMappingFabButton
            style={{
              bottom: 96,
            }}
          />
        </View>

        {showConfirmDialog ? (
          <DeleteRuleConfirmDialog
            controller={this.controller}
            data={selectedToDelete}
            cancel={this.cancel}
            confirm={this.deleteIntegrations}
            ignore={this.goToIgnorePage}
          />
        ) : null}
      </View>
    );
  }
}
